// The new-run wizard on Claims → Runs: pick a range, review the AxisCare
// visits, create one run per destination.
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. Nothing is written
// until Create; the run itself is built by lib/billing/runs.js.

import { useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { loadBillingCatalog } from '../../../lib/billingCodes';
import { hasClockPair, HOURS_BASES } from '../../../lib/axiscare';
import { AUTH_REASON_LABEL } from '../../../lib/priorAuth';
import { EXPECTED_CHARGE_MULTIPLIER } from '../../../lib/edi/preflight';
import { lineHours } from '../../../lib/billing/claimLines';
import { createRunsFromVisits } from '../../../lib/billing/runs';
import { destinationLabel } from '../../../lib/serviceModel';
import { fetchBillingVisits, billableVisits, buildClientMap, loadAuthHistory, authFor } from './visitData';
import { fmt$2 } from './format';

// Hours on a basis — lib/billing/claimLines.js, the rule the Monday run uses too.
const calcHours = lineHours;

/**
 * @param {object}   p.profile           the signed-in user, recorded on the run
 * @param {Function} p.onClose
 * @param {Function} p.onCreated         after runs are written, to reload the list
 * @param {Function} p.onExportCsv       (run) from the Done step
 * @param {Function} p.onExport837P      (run) from the Done step
 * @param {Function} p.onEnterReference  (run) opens the ICN / claim number dialog
 */
export default function NewRunWizard({ profile, onClose, onCreated, onExportCsv, onExport837P, onEnterReference }) {
  const [step,       setStep]       = useState(1);
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [fetching,   setFetching]   = useState(false);
  const [visits,     setVisits]     = useState([]);
  const [clientMap,  setClientMap]  = useState({});
  // client uuid -> its PA authorization history, loaded with the visits.
  const [authHist,   setAuthHist]   = useState({});
  const [selected,   setSelected]   = useState({});
  const [creating,   setCreating]   = useState(false);
  const [newRunId,   setNewRunId]   = useState(null);
  // Every run the wizard just created — one per destination when a week has
  // both GAMMIS and CareSource clients in it.
  const [newRuns,    setNewRuns]    = useState([]);

  // Wizard charge multiplier
  // The manual GAMMIS billing charges 2.5x the Medicaid-payable amount, on
  // 977 of the 981 priced rows in the July–December workbook. CAERA billed 1.5x,
  // which is most of why a shadow run's charge column never matched the log it
  // exists to be checked against. A run stores the multiplier it used, so past
  // runs keep the figure they went out with.
  const [chargeMultiplier] = useState(EXPECTED_CHARGE_MULTIPLIER);
  // Which window this run bills. Chosen once, at creation, and frozen onto the
  // run — a past run has to keep showing the basis it actually went out on.
  // Same default as the weekly cron (open-scheduled-runs): clocked time where
  // it exists, scheduled where it does not. Over the seven weeks from 1 August
  // that bills materially the same revenue as scheduled while using the real
  // worked time on the ~88% of lines where the two differ. The wizard still
  // asks, because a week whose visits were confirmed verbally is billed on
  // scheduled hours.
  const [hoursBasis, setHoursBasis] = useState('clocked_else_scheduled');

  // Closing the wizard unmounts it, which is what resets it.
  const resetWizard = onClose;

  // ── Fetch visits from AxisCare (new run) ──────────────────────────────────
  const fetchVisits = async () => {
    if (!dateFrom || !dateTo) return;
    setFetching(true); setVisits([]); setSelected({});
    try {
      const arr = await fetchBillingVisits(dateFrom, dateTo);
      const completed = billableVisits(arr, hoursBasis);
      const cMap = await buildClientMap(completed);
      const hist = await loadAuthHistory(cMap);
      setClientMap(cMap); setAuthHist(hist); setVisits(completed);
      const sel = {}; completed.forEach((_, i) => { sel[i] = true; });
      setSelected(sel); setStep(2);
    } catch (err) { alert('Failed to fetch from AxisCare: ' + err.message); }
    finally { setFetching(false); }
  };

  // ── Create billing run + claim lines ──────────────────────────────────────
  const createRun = async () => {
    const toImport = visits.filter((_, i) => selected[i]);
    if (!toImport.length) return;
    setCreating(true);
    try {
      const mult = chargeMultiplier || EXPECTED_CHARGE_MULTIPLIER;
      const catalog = await loadBillingCatalog();

      // One run per destination — GAMMIS for GAPP and FFS Medicaid, CareSource
      // on its own — built and written by lib/billing/runs.js.
      const created = await createRunsFromVisits(supabase, {
        visits: toImport, clientMap, authHist, catalog,
        basis: hoursBasis, multiplier: mult, dateFrom, dateTo,
        profileId: profile?.id || null, now: new Date().toISOString(),
      });

      // The GAMMIS run is the one the post-creation buttons act on; a
      // CareSource-only week has no 837P step at all.
      const primary = created.find(r => r.payer === 'gammis') || created[0];
      setNewRuns(created); setNewRunId(primary.id); setStep(3); onCreated();
    } catch (err) { alert('Failed to create billing run: ' + err.message); }
    finally { setCreating(false); }
  };

  const selectedVisits  = visits.filter((_, i) => selected[i]);
  // Each line rounded to the cent before summing, as claim lines are stored,
  // so the preview and the created run agree (it used to differ by a cent a line).
  const previewBilled   = selectedVisits.reduce((s, v) => s + Math.round(calcHours(v, hoursBasis) * (v.chargeRate || 0) * 100) / 100, 0);
  // Lines about to be billed for care with no clock record, and lines nobody
  // has reviewed. Verification does not gate a run (decided 2026-09-13), so
  // this is a count to look at rather than something to clear.
  const unclockedSelected  = selectedVisits.filter(v => !hasClockPair(v)).length;
  const unverifiedSelected = selectedVisits.filter(v => v._source === 'caera' && !v.verified).length;
  // Selected lines whose authorization could not be resolved for their own
  // service date, grouped by why. These are the CO-15 denials in advance: a
  // claim without an authorization number is rejected, so it is worth knowing
  // before the file is built rather than six weeks later on a remittance.
  const authGaps = useMemo(() => {
    const by = {};
    for (const v of selectedVisits) {
      const { number, reason } = authFor(v, clientMap, authHist);
      if (number) continue;
      const name = v.client ? `${v.client.lastName || ''}, ${v.client.firstName || ''}`.trim() : 'Unknown';
      (by[reason] = by[reason] || new Set()).add(name);
    }
    return Object.entries(by).map(([reason, names]) => ({ reason, names: [...names] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVisits, clientMap, authHist]);
  const authGapCount = authGaps.reduce((n, g) => n + g.names.length, 0);

  return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && resetWizard()}>
        <div className="modal" style={{ maxWidth: step === 2 ? 780 : 520 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {[1,2,3].map(n => (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
                    background: step >= n ? 'var(--accent)' : 'var(--bg2)', color: step >= n ? '#fff' : 'var(--text3)' }}>{n}</div>
                  <span style={{ fontSize: 11, color: step === n ? 'var(--text)' : 'var(--text3)', fontWeight: step === n ? 600 : 400 }}>
                    {n === 1 ? 'Date Range' : n === 2 ? 'Review Visits' : 'Done'}
                  </span>
                  {n < 3 && <span style={{ color: 'var(--border)', fontSize: 11 }}>›</span>}
                </div>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={resetWizard}>✕</button>
          </div>
          <div className="modal-body">
            {step === 1 && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                  Select the billing period. CAERA will pull all completed, billable visits from AxisCare for that range.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">From date <span className="req">*</span></label>
                    <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">To date <span className="req">*</span></label>
                    <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: 4 }}>
                  <label className="form-label" htmlFor="hours-basis">Bill which hours? <span className="req">*</span></label>
                  <select id="hours-basis" className="form-input" value={hoursBasis} onChange={e => setHoursBasis(e.target.value)}>
                    {HOURS_BASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
                  Visits need a charge rate &gt; $0. Missed and cancelled visits are never billed,
                  whichever basis you pick. This choice is saved on the run.
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                    <strong>{visits.length}</strong> billable visits found for {dateFrom} – {dateTo}.
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
                    {Object.values(selected).filter(Boolean).length} selected · Accrued: {fmt$2(previewBilled)}
                  </div>
                </div>
                {/* The two things worth seeing before a claim file is built.
                    Neither blocks the run — they are counts, not gates — but
                    billing care nobody clocked should be a decision someone
                    made, not a default nobody saw. */}
                {authGapCount > 0 && (
                  <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', color: 'var(--danger)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      ⚠ {authGapCount} {authGapCount === 1 ? 'client has' : 'clients have'} no authorization number for these service dates — those claims will deny CO-15
                    </div>
                    {authGaps.map(g => (
                      <div key={g.reason} style={{ marginTop: 2 }}>
                        {AUTH_REASON_LABEL[g.reason] || g.reason}: {g.names.slice(0, 5).join('; ')}
                        {g.names.length > 5 && ` and ${g.names.length - 5} more`}
                      </div>
                    ))}
                    <div style={{ marginTop: 3, opacity: 0.85 }}>Fix on Prior Auth, then fetch again — or bill them and add the number per line.</div>
                  </div>
                )}
                {(unclockedSelected > 0 || unverifiedSelected > 0) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)' }}>
                    {unclockedSelected > 0 && (
                      <span style={{ color: 'var(--warn)' }}>
                        ⚠ <strong>{unclockedSelected}</strong> selected {unclockedSelected === 1 ? 'visit has' : 'visits have'} no clock record — billed on scheduled hours
                      </span>
                    )}
                    {unverifiedSelected > 0 && (
                      <span style={{ color: 'var(--text3)' }}>
                        {unverifiedSelected} not yet reviewed on the verification desk
                      </span>
                    )}
                  </div>
                )}
                {visits.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No billable completed visits found for this date range.</div>
                ) : (
                  <div style={{ maxHeight: 380, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>
                            <input type="checkbox"
                              checked={Object.values(selected).length > 0 && Object.values(selected).every(Boolean)}
                              onChange={e => { const s = {}; visits.forEach((_, i) => { s[i] = e.target.checked; }); setSelected(s); }} />
                          </th>
                          {['Client','Medicaid ID','Date','Code','Hours','Amount','Matched'].map(h => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {visits.map((v, i) => {
                          const h = calcHours(v, hoursBasis);
                          const date = (v.actualStartDate || v.scheduledStartDate || v.startDate || '').slice(0, 10);
                          const client = v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '—';
                          const hcMatch = clientMap[String(v.client?.id || '')];
                          return (
                            <tr key={i} style={{ borderTop: '0.5px solid var(--border)', opacity: selected[i] ? 1 : 0.45 }}>
                              <td style={{ padding: '6px 12px' }}>
                                <input type="checkbox" checked={!!selected[i]} onChange={e => setSelected(p => ({ ...p, [i]: e.target.checked }))} />
                              </td>
                              <td style={{ padding: '6px 10px', fontWeight: 500 }}>{client}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>
                                {hcMatch?.medicaid_id || <span style={{ color: 'var(--danger)', fontSize: 10 }}>no match</span>}
                              </td>
                              <td style={{ padding: '6px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{date}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11 }}>{v.service?.code || '—'}</td>
                              <td style={{ padding: '6px 10px' }}>{h.toFixed(1)}h</td>
                              <td style={{ padding: '6px 10px', fontWeight: 500, color: 'var(--success)' }}>{fmt$2(h * (v.chargeRate || 0))}</td>
                              <td style={{ padding: '6px 10px', fontSize: 10 }}>
                                {hcMatch?.id && hcMatch.viaName
                                  ? <span style={{ color: 'var(--warn)', fontWeight: 500 }} title="Matched by name only — this client has no AxisCare ID linked yet. Verify before approving.">⚠ name match</span>
                                  : hcMatch?.id
                                  ? <span style={{ color: 'var(--success)', fontWeight: 500 }}>✓ matched</span>
                                  : <span style={{ color: 'var(--danger)' }}>unmatched</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                  Unmatched visits will still be included — fill in Medicaid ID manually after creating the run.
                </div>
              </>
            )}
            {step === 3 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)', marginBottom: 8 }}>
                  {newRuns.length > 1 ? `${newRuns.length} Billing Runs Created` : 'Billing Run Created'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
                  {newRuns.map(r => (
                    <div key={r.id}>{r._lines} claim line{r._lines === 1 ? '' : 's'} → <b>{destinationLabel(r.payer)}</b> for {dateFrom} – {dateTo}</div>
                  ))}
                  {newRuns.some(r => r.payer === 'caresource') && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                      The CareSource run is filed with CareSource outside CAERA. Export its CSV, file it there, then record the claim number on the run.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-outline" onClick={() => { const run = newRuns.find(r => r.id === newRunId); if (run) onExportCsv(run); }}>
                    ↓ CSV
                  </button>
                  {newRuns.some(r => r.payer === 'gammis') && (
                    <button className="btn btn-outline" style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
                      onClick={() => { const run = newRuns.find(r => r.payer === 'gammis'); if (run) onExport837P(run); }}>
                      ↓ 837P EDI
                    </button>
                  )}
                  <button className="btn btn-primary" onClick={() => { const run = newRuns.find(r => r.id === newRunId); if (run) onEnterReference(run); resetWizard(); }}>
                    {newRuns.find(r => r.id === newRunId)?.payer === 'caresource' ? 'Record claim no. →' : 'Enter ICN →'}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16 }}>
                  {newRuns.some(r => r.payer === 'gammis')
                    ? 'GAMMIS runs pass pre-flight and go over SFTP, or upload the 837P in the portal and enter the batch reference here.'
                    : 'File the CSV with CareSource, then return here to record the claim number.'}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {step === 1 && (
              <>
                <button className="btn btn-outline" onClick={resetWizard}>Cancel</button>
                <button className="btn btn-primary" onClick={fetchVisits} disabled={fetching || !dateFrom || !dateTo}>
                  {fetching ? 'Fetching from AxisCare…' : 'Fetch Visits →'}
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={createRun} disabled={creating || Object.values(selected).every(v => !v)}>
                  {creating ? 'Creating…' : `Create Billing Run (${Object.values(selected).filter(Boolean).length} lines)`}
                </button>
              </>
            )}
            {step === 3 && <button className="btn btn-primary" onClick={resetWizard}>Done</button>}
          </div>
        </div>
      </div>
  );
}
